import React from 'react';
import { periodTotals } from './tripTotals';

// Per-project overview — the landing screen shown when a project is opened.
// Reads existing project state and summarises status before the planner
// jumps into the map workspace. Purely derived from data already in App.js;
// it never computes anything new.

const PU = '#3D1A6E', PU2 = '#7B4DBF', PU_SOFT = '#F5F0FA', PU_LINE = '#EDE5F8';
const CREAM = '#F6F1E9', INK = '#1C1C2E', MUT = '#6B6880', TEAL = '#0D9488';

function Card({ title, children, style }) {
  return (
    <div style={{ background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 14, padding: '18px 20px', boxShadow: '0 2px 14px rgba(61,26,110,0.04)', ...style }}>
      {title && <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PU, marginBottom: 16 }}>{title}</div>}
      {children}
    </div>
  );
}

function Step({ n, label, hint, state }) {
  const done = state === 'done', act = state === 'act';
  return (
    <div style={{ textAlign: 'center', position: 'relative', padding: '0 6px' }}>
      {n > 1 && <div style={{ position: 'absolute', top: 16, left: '-50%', width: '100%', height: 3, background: (done || act) ? TEAL : PU_LINE, zIndex: 0 }} />}
      <div style={{ width: 34, height: 34, borderRadius: '50%', margin: '0 auto 9px', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, border: '3px solid #fff', background: done ? TEAL : act ? PU : PU_SOFT, color: (done || act) ? '#fff' : PU2, boxShadow: act ? `0 0 0 4px ${PU_SOFT}` : 'none' }}>
        {done ? '✓' : n}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: INK }}>{label}</div>
      <div style={{ fontSize: 10.5, color: MUT, marginTop: 3 }}>{hint}</div>
    </div>
  );
}

export default function ProjectDashboard({
  name, projectNumber, client, preparedBy, checkedBy,
  scenarios = [], timePeriods = [], devSize,
  pinLocation, selectedOAs = [], modeShare, odFlows,
  onOpenMap, onOpenResults, onBackToProjects, onEditDetails,
}) {
  // Derive progress from what actually exists in project state.
  const hasSite = !!pinLocation;
  const hasDist = (selectedOAs && selectedOAs.length > 0);
  const hasMode = !!modeShare;
  const hasAssign = !!odFlows;
  const states = [hasSite, hasDist, hasMode, hasAssign];
  const firstIncomplete = states.indexOf(false);
  function stepState(i) {
    if (states[i]) return 'done';
    if (i === firstIncomplete) return 'act';
    return '';
  }

  const periods = timePeriods.length > 0 ? timePeriods : [];
  const firstSc = scenarios[0];
  const firstTp = periods[0];
  const keyTotals = (firstSc && firstTp) ? periodTotals(firstSc, firstTp, scenarios) : null;

  const label = { fontSize: 12.5, color: MUT };
  const strong = { color: PU, fontWeight: 700 };

  return (
    <div style={{ minHeight: '100vh', background: CREAM, fontFamily: 'Nunito Sans, Avenir, sans-serif' }}>

      {/* Top bar */}
      <div style={{ height: 54, background: '#fff', borderBottom: `1.5px solid ${PU_LINE}`, borderLeft: `4px solid ${PU}`, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px' }}>
        <button onClick={onBackToProjects} style={{ background: 'none', border: `1.5px solid ${PU_LINE}`, borderRadius: 7, color: PU, fontSize: 11, fontWeight: 800, padding: '7px 11px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>← Projects</button>
        <div style={{ width: 1, height: 24, background: PU_LINE }} />
        <svg width="17" height="21" viewBox="0 0 197 249"><path d="M158.29,177.25c4.37,3.62,3.99,11.25,1.09,14.9-3.48,4.37-9.56,4.58-13.73,1.1l-48.6-40.52-.24,71.04c-.02,5.58-6.19,8.2-10.14,7.99-5.27-.27-9.86-3.75-9.89-9.84l-.29-69.51-47.82,41.21c-4.27,3.68-9.47,3.15-13.56-.41-2.97-2.58-4.95-10.22-.59-13.94l56.23-47.93L15.15,85.99c-4.5-3.67-3.42-10.67-.58-13.96,3.79-4.38,9.86-4.61,14.39-.94l47.62,38.59.09-91.63c0-4.41,1.79-8.05,5.23-9.8,2.94-1.5,7.95-1.51,10.98.9l29.47,23.46c5.92,4.72,10.1,11.62,4.75,18.78-3.06,4.09-9.99,3.9-14.06.58l-16.07-13.13v70.41s46.81-40.03,46.81-40.03l-12.93-12.02,31.27-6.11,22.14-4.2-17.67,53.75-8.72-17.32-55.58,47.57,56,46.38Z" fill="#3D1A6E" /></svg>
        <div style={{ fontSize: 13, fontWeight: 800, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>{name}</div>
      </div>

      <div style={{ maxWidth: 940, margin: '0 auto', padding: '26px 26px 50px' }}>

        {/* Hero */}
        <div style={{ background: '#fff', border: `1.5px solid ${PU_LINE}`, borderRadius: 14, padding: '22px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18, marginBottom: 16, boxShadow: '0 2px 14px rgba(61,26,110,0.05)' }}>
          <div>
            <h1 style={{ fontSize: 23, fontWeight: 800, margin: '0 0 8px', color: INK }}>{name}</h1>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {client && <span style={label}>Client <b style={strong}>{client}</b></span>}
              {projectNumber && <span style={label}>Project <b style={strong}>#{projectNumber}</b></span>}
              {preparedBy && <span style={label}>Prepared by <b style={strong}>{preparedBy}</b></span>}
              {checkedBy && <span style={label}>Checked by <b style={strong}>{checkedBy}</b></span>}
              <button onClick={onEditDetails} style={{ background: 'none', border: 'none', color: PU2, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>✏️ Edit details</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
            <button onClick={onOpenMap} style={{ background: PU, color: '#fff', border: 'none', borderRadius: 10, padding: '13px 20px', fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              Open map workspace →
            </button>
            <button onClick={onOpenResults} style={{ background: '#fff', color: PU, border: `1.5px solid ${PU_LINE}`, borderRadius: 10, padding: '11px 20px', fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              View results
            </button>
          </div>
        </div>

        {/* Progress */}
        <Card title="Assessment progress" style={{ marginBottom: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <Step n={1} label="Site & trips" hint={hasSite ? `${devSize ? devSize + ' units' : 'site set'}` : 'Not set'} state={stepState(0)} />
            <Step n={2} label="Distribution" hint={hasDist ? `${selectedOAs.length} output areas` : 'Not started'} state={stepState(1)} />
            <Step n={3} label="Mode share" hint={hasMode ? 'Set' : 'Not started'} state={stepState(2)} />
            <Step n={4} label="Assignment" hint={hasAssign ? 'Flows generated' : 'Not started'} state={stepState(3)} />
          </div>
        </Card>

        {/* Scenarios */}
        <Card title="Scenarios" style={{ marginBottom: 16 }}>
          {scenarios.length === 0 ? (
            <div style={{ fontSize: 13, color: MUT }}>No scenarios defined yet. Open the map workspace to set up scenarios and time periods.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
              {scenarios.map((sc, i) => (
                <div key={sc.id} style={{ border: `1.5px solid ${i === 0 ? PU : PU_LINE}`, borderRadius: 11, padding: '15px 16px', background: i === 0 ? PU_SOFT : '#fff' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 11 }}>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: PU }}>{sc.name || `Scenario ${i + 1}`}</span>
                    {i === 0 && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', background: PU, color: '#fff', borderRadius: 5, padding: '3px 8px' }}>Primary</span>}
                  </div>
                  {periods.length === 0 ? (
                    <div style={{ fontSize: 12, color: MUT }}>No time periods yet</div>
                  ) : periods.map(tp => {
                    const t = periodTotals(sc, tp, scenarios);
                    return (
                      <div key={tp.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '5px 0', borderTop: `1px solid ${PU_LINE}` }}>
                        <span style={{ color: MUT }}>{tp.name}</span>
                        <b style={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{t.total} trips/hr</b>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Key figures */}
        {keyTotals && (
          <Card title={`Key figures — ${firstSc.name || 'Scenario 1'}, ${firstTp.name}`}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
              {[['Total trips', keyTotals.total], ['Arrivals', keyTotals.arr], ['Departures', keyTotals.dep]].map(([k, v]) => (
                <div key={k} style={{ background: PU_SOFT, borderRadius: 11, padding: '14px 15px' }}>
                  <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: PU2 }}>{k}</div>
                  <div style={{ fontSize: 25, fontWeight: 800, color: PU, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{v}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
